/**
 * Crosshair plugin for showing a horizontal guide and Y value on hover.
 * Used on device charts to make readings easier to read.
 */
export const crosshairYPlugin = {
    id: 'crosshairY',

    afterEvent(chart, args) {
        const pluginOpts = chart.options.plugins?.crosshairY;
        if (!pluginOpts?.enabled) return;

        const { event } = args;
        const { chartArea } = chart;

        // Clear guides when the pointer leaves the chart
        if (event.type === 'mouseout' || event.type === 'mouseleave') {
            chart._crosshairY = null;
            chart.draw();
            return;
        }

        if (event.type === 'mousemove') {
            const mouseX = event.x;
            const mouseY = event.y;

            // Ignore if pointer is outside the plot area
            if (
                mouseX < chartArea.left ||
                mouseX > chartArea.right ||
                mouseY < chartArea.top ||
                mouseY > chartArea.bottom
            ) {
                chart._crosshairY = null;
                chart.draw();
                return;
            }

            // Select the Y axis (default to 'y' when multiple scales exist)
            const yAxisId = pluginOpts.yAxisId || 'y';
            const yScale = chart.scales[yAxisId];
            if (!yScale) {
                chart._crosshairY = null;
                chart.draw();
                return;
            }

            const yValue = yScale.getValueForPixel(mouseY);

            chart._crosshairY = {
                x: mouseX,
                y: mouseY,
                yValue,
                yAxisId,
            };
            chart.draw();
        }
    },

    afterDatasetsDraw(chart) {
        if (!chart._crosshairY) return;

        const pluginOpts = chart.options.plugins?.crosshairY || {};
        const { ctx, chartArea, scales } = chart;
        const { x, y, yValue, yAxisId } = chart._crosshairY;

        const lineColor = pluginOpts.lineColor || 'rgba(0, 0, 0, 0.5)';
        const lineWidth = pluginOpts.lineWidth || 1;
        const lineDash = pluginOpts.lineDash || [5, 5];
        const showVerticalLine = pluginOpts.showVerticalLine !== false;
        const showHorizontalLine = pluginOpts.showHorizontalLine !== false;
        const labelBgColor = pluginOpts.labelBgColor || '#333';
        const labelTextColor = pluginOpts.labelTextColor || '#fff';
        const labelFont = pluginOpts.labelFont || 'bold 11px Arial';
        const decimals = pluginOpts.decimals ?? 2;
        const unit = pluginOpts.unit || '';

        ctx.save();

        // Shared line styles
        ctx.strokeStyle = lineColor;
        ctx.lineWidth = lineWidth;
        ctx.setLineDash(lineDash);

        // Horizontal guide
        if (showHorizontalLine) {
            ctx.beginPath();
            ctx.moveTo(chartArea.left, y);
            ctx.lineTo(chartArea.right, y);
            ctx.stroke();
        }

        // Vertical guide
        if (showVerticalLine) {
            ctx.beginPath();
            ctx.moveTo(x, chartArea.top);
            ctx.lineTo(x, chartArea.bottom);
            ctx.stroke();
        }

        ctx.setLineDash([]);

        // Y-axis value label
        if (showHorizontalLine && yValue != null) {
            const yScale = scales[yAxisId];
            if (yScale) {
                // Format value
                let text = Number(yValue).toFixed(decimals);
                if (unit) text = `${text} ${unit}`;

                ctx.font = labelFont;
                const textWidth = ctx.measureText(text).width;
                const padding = 4;
                const boxWidth = textWidth + padding * 2;
                const boxHeight = 18;

                // Position label to the left of the Y axis
                const boxX = chartArea.left - boxWidth - 4;
                const boxY = y - boxHeight / 2;

                // Draw label background
                ctx.fillStyle = labelBgColor;
                ctx.beginPath();
                ctx.roundRect(boxX, boxY, boxWidth, boxHeight, 3);
                ctx.fill();

                // Draw label text
                ctx.fillStyle = labelTextColor;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(text, boxX + boxWidth / 2, y);
            }
        }

        ctx.restore();
    },
};
